import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import { Card, CardContent, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@xxtract/ui';
import type { Lijst } from '@xxtract/tabelfilters';
import { ApiErrorMessage } from '@/components/feedback/ApiErrorMessage';
import { FilterRejectedMessage } from '@/components/feedback/FilterRejectedMessage';
import { ariaSort, Filterbalk, SorteerbareKop, useStartadres, useTabelData, useTabelStand } from '@/components/tabel';
import { formatDate } from '@/lib/formatters';
import { useIntakeList } from '../hooks/useIntake';
import { IntakeStatusBadge } from '../components/IntakeStatusBadge';
import { KiesEersteKlant } from '../components/KiesEersteKlant';
import { useZonderGekozenKlant } from '../hooks/useZonderGekozenKlant';
import { EmptyStatePage } from '@/features/navigation/pages/EmptyStatePage';
import { useToast } from '@/components/feedback/Toaster';
import { downloadSubmissionFile, type IntakeListRow, type IntakeSessionFlow } from '../intake.api';

const NEW_INTAKE_LINK_CLASSNAME =
  'inline-flex h-9 items-center rounded-[var(--radius-control)] bg-[var(--primary)] px-4 text-sm font-medium text-white hover:bg-[var(--primary-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)]';

/**
 * De vertaalsleutel per flow, als volledige `Record` zodat de compiler een vierde flow afdwingt.
 * Stond hier eerst als sjabloonsleutel: toen marketing erbij kwam ontbrak de vertaling, en op
 * acceptatie las de kolom Type letterlijk `intake.list.flow.marketing` (12-09-2026).
 */
const FLOW_LABEL_KEYS: Record<IntakeSessionFlow, string> = {
  product: 'intake.list.flow.product',
  packaging: 'intake.list.flow.packaging',
  marketing: 'intake.list.flow.marketing',
};

/**
 * Filterwaarden van de statuskolom: de sessiestatussen plus 'received' (ingediend mét ordernummer)
 * en 'notCompleted' (CustomerPortal telt nog niet afgeronde producten).
 */
const INTAKE_STATUSWAARDEN = ['uploaded', 'interpreting', 'review', 'submitting', 'submitted', 'expired', 'received', 'notCompleted'];

/**
 * spec-tabelfilters story 4: de kolommen van de intake-lijst, eenmalig beschreven (§2).
 * spec-cp-aanmeldingen-in-lijst: één rij per aanmelding, uit het portaal of uit CustomerPortal.
 * Geen standaardsortering: de API levert de rijen al op `submittedAt ?? lastActivityAt`, nieuwste
 * eerst, en die volgorde kan de tabel niet uit één kolom nabootsen.
 */
export const INTAKE_LIJST: Lijst<IntakeListRow> = {
  kolommen: [
    { sleutel: 'bestand', kopSleutel: 'intake.list.columns.filename', type: 'tekst', waarde: (r) => r.originalFilename ?? r.orderNumber },
    { sleutel: 'stroom', kopSleutel: 'intake.list.columns.flow', type: 'keuzelijst', waarde: (r) => r.flow, waardeLabel: (w, t) => t(FLOW_LABEL_KEYS[w as IntakeSessionFlow] ?? w), vasteWaarden: Object.keys(FLOW_LABEL_KEYS) },
    // Zoals `IntakeStatusBadge`: ingediend mét ordernummer leest als "Ontvangen" (h6-3).
    {
      sleutel: 'status',
      kopSleutel: 'intake.list.columns.status',
      type: 'keuzelijst',
      waarde: (r) => (r.status === 'submitted' && r.orderNumber ? 'received' : r.status),
      waardeLabel: (w, t) => t(`intake.list.status.${w}`),
      vasteWaarden: INTAKE_STATUSWAARDEN,
    },
    { sleutel: 'producten', kopSleutel: 'intake.list.columns.productCount', type: 'getal', waarde: (r) => r.productCount },
    { sleutel: 'rijen', kopSleutel: 'intake.list.columns.rowCount', type: 'getal', waarde: (r) => r.rowCount },
    { sleutel: 'ingediend', kopSleutel: 'intake.list.columns.submittedAt', type: 'datum', waarde: (r) => r.submittedAt },
    { sleutel: 'laatsteActiviteit', kopSleutel: 'intake.list.columns.lastActivity', type: 'datum', waarde: (r) => r.lastActivityAt },
    { sleutel: 'verloopt', kopSleutel: 'intake.list.columns.expiresAt', type: 'datum', waarde: (r) => r.expiresAt },
  ],
};
const FILTER_TEST_ID = 'intake';
const STAND_SLEUTEL = 'intake';
const STARTADRES = [{ lijst: INTAKE_LIJST, standSleutel: STAND_SLEUTEL }];
const MELDING_CLASSNAME = 'mb-4 rounded-md bg-[var(--info-light)] p-3 text-[var(--info-text)]';

/** Waar een rij heen wijst: met ordernummer het statusscherm, anders het bewerkscherm. */
function doelVan(rij: IntakeListRow): string | null {
  // spec-verpakkingsaanmeldingen-in-lijst CAP-5: een verpakkingsrij uit CustomerPortal is geen link, alleen de
  // downloadknop. Een losse portaalsessie blijft zoals hij was (CAP-3(e)), ook als zijn flow packaging is.
  if (rij.key.startsWith('packaging:')) return null;
  // h6-4: is de aanmelding ingediend, dan wil de klant de status zien en niet meer het
  // bewerkscherm. Het ordernummer is daarvoor het teken: dat bestaat pas ná het indienen.
  if (rij.orderNumber) return `/intake/orders/${encodeURIComponent(rij.orderNumber)}`;
  return rij.sessionId ? `/intake/${rij.sessionId}` : null;
}

/** spec-s3-archief-aanmeldingen CAP-5/CAP-7: alleen een regel met ordernummer én bewaard bestand. */
function DownloadKnop({ rij }: { rij: IntakeListRow }) {
  const { t } = useTranslation();
  const { addToast } = useToast();
  // Uit zolang het verzoek loopt: een dubbelklik haalt en bewaart het bestand anders twee keer.
  const [bezig, setBezig] = useState(false);
  const orderNumber = rij.orderNumber;
  // Vastgelegd door XXtract: geen bestand om te downloaden, dus geen knop.
  if (!rij.hasFile || !orderNumber || rij.origin === 'capture') return null;
  const label = t('intake.list.downloadExcel');
  return (
    <button
      aria-busy={bezig}
      aria-label={label}
      className="ml-2 inline-flex h-7 w-7 items-center justify-center rounded-[var(--radius-control)] align-middle text-[var(--secondary)] hover:bg-[var(--background-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)] disabled:opacity-50"
      data-testid={`intake-download-${rij.key}`}
      disabled={bezig}
      onClick={() => {
        setBezig(true);
        downloadSubmissionFile(orderNumber, rij.flow)
          .catch(() => addToast(t('intake.list.downloadFailed'), 'error'))
          .finally(() => setBezig(false));
      }}
      title={label}
      type="button"
    >
      <Download aria-hidden="true" className="h-4 w-4" />
    </button>
  );
}

function IntakeRow({ rij }: { rij: IntakeListRow }) {
  const { t } = useTranslation();
  const isExpired = rij.status === 'expired';
  const naam = rij.originalFilename ?? t('intake.list.submissionName', { orderNumber: rij.orderNumber });
  const doel = isExpired ? null : doelVan(rij);

  return (
    <TableRow data-testid={`intake-row-${rij.key}`}>
      <TableCell>
        {doel ? (
          <Link className="text-[var(--secondary)] hover:underline" to={doel}>
            {naam}
          </Link>
        ) : (
          <span data-testid={`intake-expired-filename-${rij.key}`}>{naam}</span>
        )}
        <DownloadKnop rij={rij} />
        {rij.origin === 'capture' ? (
          <p className="text-sm text-[var(--foreground-muted)]" data-testid={`intake-origin-${rij.key}`}>
            {t('intake.list.originCapture')}
          </p>
        ) : null}
      </TableCell>
      <TableCell>{t(FLOW_LABEL_KEYS[rij.flow])}</TableCell>
      <TableCell>
        <IntakeStatusBadge status={rij.status} orderNumber={rij.orderNumber} />
      </TableCell>
      <TableCell>{rij.productCount}</TableCell>
      <TableCell>{rij.rowCount}</TableCell>
      <TableCell>{rij.submittedAt ? formatDate(rij.submittedAt) : null}</TableCell>
      <TableCell>{rij.lastActivityAt ? formatDate(rij.lastActivityAt) : null}</TableCell>
      <TableCell>
        {isExpired ? (
          <div>
            <p className="text-[var(--foreground-muted)]">{t('intake.list.expiredNotice')}</p>
            <Link className={`${NEW_INTAKE_LINK_CLASSNAME} mt-1`} to="/intake/new">
              {t('intake.list.reuploadCta')}
            </Link>
          </div>
        ) : rij.expiresAt ? (
          // CAP-8: een ingediende aanmelding verloopt niet meer; de API geeft dan `expiresAt: null`.
          <>
            <span>{formatDate(rij.expiresAt)}</span>
            {rij.expiryWarning ? (
              <span data-testid={`intake-expiry-warning-${rij.key}`} className="ml-2 text-[var(--warning-text)]">
                {t('intake.list.expiryWarning')}
              </span>
            ) : null}
          </>
        ) : null}
      </TableCell>
    </TableRow>
  );
}

export function IntakeListPage() {
  const { t } = useTranslation();
  const { data: lijst, isLoading, isError, error, refetch } = useIntakeList();
  const data = lijst?.data;
  const kopRef = useRef<HTMLHeadingElement>(null);
  const { start, geweigerd, wisGeweigerd } = useStartadres(STARTADRES);
  const stand = useTabelStand(INTAKE_LIJST, STAND_SLEUTEL, { start: start[STAND_SLEUTEL] });
  const context = useMemo(() => ({ t: (sleutel: string) => t(sleutel) }), [t]);
  const tabel = useTabelData(data, INTAKE_LIJST, stand, context);
  const zonderKlant = useZonderGekozenKlant();

  if (zonderKlant) {
    return <KiesEersteKlant />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="intake-list-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--border)] border-t-[var(--primary)]" />
      </div>
    );
  }

  if (isError) {
    return <ApiErrorMessage error={error} onRetry={() => refetch()} />;
  }

  const incomplete = lijst?.meta.incomplete ?? false;
  const heeftRijen = (data ?? []).length > 0;
  // CAP-6: kon niet alles geladen worden, dan is "nog geen aanmeldingen" misschien niet waar.
  if (!heeftRijen && !incomplete) {
    return <EmptyStatePage kind="intake" />;
  }

  return (
    <section data-testid="intake-list-page">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-bold text-[var(--foreground-heading)]" ref={kopRef} tabIndex={-1}>{t('intake.list.title')}</h1>
        <Link className={NEW_INTAKE_LINK_CLASSNAME} to="/intake/new">
          {t('intake.list.newButton')}
        </Link>
      </div>
      {incomplete ? (
        <p className={MELDING_CLASSNAME} data-testid="intake-list-incomplete" role="status">
          {t('intake.list.incomplete')}
        </p>
      ) : null}
      {lijst?.meta.truncated ? (
        <p className={MELDING_CLASSNAME} data-testid="intake-list-truncated" role="status">
          {t('intake.list.truncated')}
        </p>
      ) : null}
      {heeftRijen ? (
        <>
          <FilterRejectedMessage
            baseTestId={FILTER_TEST_ID}
            focusNaWissen={kopRef}
            linkGeweigerd={geweigerd}
            onClear={() => {
              stand.reset();
              wisGeweigerd();
            }}
          />
          <div className="mb-4">
            <Filterbalk context={context} data={tabel} lijst={INTAKE_LIJST} stand={stand} />
          </div>
          <Card>
            <CardContent className="overflow-x-auto p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    {INTAKE_LIJST.kolommen.map((k) => (
                      <TableHead aria-sort={ariaSort(stand.sortering, k.sleutel)} key={k.sleutel} scope="col">
                        <SorteerbareKop kolom={k} onKlik={() => stand.klikOpKop(k.sleutel)} sortering={stand.sortering} />
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tabel.rijen.length === 0 ? (
                    <TableRow>
                      <TableCell className="py-8 text-center text-[var(--foreground-muted)]" colSpan={INTAKE_LIJST.kolommen.length}>
                        {t('common.noResults')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    tabel.rijen.map((rij) => <IntakeRow key={rij.key} rij={rij} />)
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      ) : null}
    </section>
  );
}
